import type { ProductTourPageId } from "./product-tour";
import type { ControlTourStep } from "./control-tour.model";

const setting = (section: string, label: string, body: string, target = "label"): ControlTourStep => ({
  target,
  label,
  title: label,
  body,
  reveal: `#settings-tab-${section}`,
});

export const WORKSPACE_CONTROL_TOURS: Partial<Record<ProductTourPageId, ControlTourStep[]>> = {
  chats: [
    {
      target: '[data-tour="chats-new"]',
      title: "New conversation",
      body: "Open a fresh conversation with Nikola when you want to start a new coaching question.",
    },
    {
      target: '[aria-label^="Open "]:not([aria-label^="Open your game"]):not([aria-label^="Open coaching"])',
      title: "Resume a conversation",
      body: "Open a saved conversation to read earlier messages and continue its coaching thread.",
    },
    {
      target: '[aria-label^="Open your game against "]',
      title: "Open the related game",
      body: "Open the game review linked to this conversation to inspect its recorded evidence.",
    },
    {
      target: '[aria-label^="Analyze game against "]',
      title: "Analyze the related game",
      body: "Open analysis for this imported game before asking for verified review evidence.",
    },
    {
      target: '[aria-label^="Delete "]',
      title: "Delete a conversation",
      body: "Request deletion of this saved conversation. Review the confirmation before proceeding.",
    },
  ],
  chat: [
    {
      target: '[aria-label="Back to Nikola conversations"]',
      title: "Conversation history",
      body: "Return to your saved conversations to resume another coaching thread.",
    },
    {
      target: '[aria-label$="Nikola credits available"]',
      title: "Available credits",
      body: "Check your remaining Nikola credits before sending a coaching request.",
    },
    {
      target: '[aria-label="Choose coaching context"]',
      title: "Choose coaching context",
      body: "Choose the account or game context for your next question so Nikola can use the appropriate evidence.",
    },
    {
      target: '[aria-label="Game Review context"] button:not([aria-label])',
      title: "Use a review prompt",
      body: "Place a suggested question about the attached game into your draft. Review it before sending.",
    },
    {
      target: '[aria-label="Dismiss review context"]',
      title: "Dismiss review context",
      body: "Hide the context strip when you no longer need its review prompts.",
    },
    {
      target: "#nikola-chat-input",
      title: "Message Nikola",
      body: "Write a specific question about your game or decision. Check the context and credit note before sending.",
    },
    {
      target: '[aria-label="Send message"]',
      title: "Send message",
      body: "Send your draft to Nikola. Availability depends on your credits and whether another request or analysis is still running.",
    },
    {
      target: '[aria-label="Stop Nikola"]',
      title: "Stop Nikola",
      body: "Stop the active coaching request when you no longer need it to continue.",
    },
  ],
  membership: [
    {
      target: '[data-tour="membership-status"] .MuiTypography-caption',
      title: "Membership status",
      body: "Check your current membership before choosing a subscription or managing billing.",
    },
    {
      target: "button",
      label: "Manage membership",
      title: "Manage membership",
      body: "Open billing management for an active membership to review the available subscription controls.",
    },
    {
      target: "button",
      label: "Join monthly · $12",
      title: "Join monthly",
      body: "Open checkout for the monthly membership. Review the price and terms in checkout before purchasing.",
    },
    {
      target: "button",
      label: "Join yearly · $96",
      title: "Join yearly",
      body: "Open checkout for the yearly membership. Review the billing commitment before purchasing.",
    },
    {
      target: "button",
      label: "Move yearly · $96",
      title: "Move to yearly",
      body: "Change an eligible monthly membership to yearly billing. This changes your billing plan when you choose it.",
    },
    {
      target: '[data-tour="membership-usage"] p',
      title: "Credits available",
      body: "Check the remaining Nikola credits and the next scheduled credit grant.",
    },
    {
      target: '[data-tour="membership-usage"] button',
      title: "Buy additional credits",
      body: "Choose a displayed credit pack when top-ups are available for your account. Review checkout before purchasing.",
    },
  ],
  profile: [
    {
      target: 'a[href^="https://www.chess.com/member/"]',
      title: "Public chess profile",
      body: "Open the Chess.com profile linked to your Chessla account.",
    },
    {
      target: "label",
      label: "First name",
      title: "First name",
      body: "Edit your first name, then use Save details to store the change.",
    },
    {
      target: "label",
      label: "Last name",
      title: "Last name",
      body: "Edit your last name, then use Save details to store the change.",
    },
    {
      target: "label",
      label: "Date of birth",
      title: "Date of birth",
      body: "Update the date stored with your account details before saving.",
    },
    {
      target: "label",
      label: "Login email",
      title: "Login email",
      body: "This read-only field identifies the email used to sign in.",
    },
    {
      target: "button",
      label: "SAVE DETAILS",
      title: "Save details",
      body: "Save the account details you edited. Wait for the save to finish before leaving.",
    },
    {
      target: "label",
      label: "Current password",
      title: "Current password",
      body: "Enter your current password to authorize a password change.",
    },
    {
      target: "label",
      label: "New password",
      title: "New password",
      body: "Enter the password you want to use for future sign-ins.",
    },
    {
      target: "label",
      label: "Confirm new password",
      title: "Confirm new password",
      body: "Repeat the new password so the form can check that both entries match.",
    },
    {
      target: "button",
      label: "CHANGE PASSWORD",
      title: "Change password",
      body: "Submit the current and new passwords to update your sign-in credentials.",
    },
    {
      target: "label",
      label: "Chess.com username",
      title: "Chess.com source",
      body: "Choose the public Chess.com account used for imported games. Changing this source replaces the current chess dataset.",
    },
    {
      target: "label",
      label: "I understand this replaces the current chess dataset",
      title: "Confirm dataset replacement",
      body: "Read the replacement warning before checking this acknowledgment. Changing source affects the existing chess dataset.",
    },
    {
      target: "button",
      label: "CHANGE CHESS SOURCE",
      title: "Change chess source",
      body: "Apply the new Chess.com source only after reviewing the replacement warning and required acknowledgment.",
    },
    {
      target: '[data-tour="profile-settings"]',
      title: "Open settings",
      body: "Open board preferences, remembered filters, notifications, and Nikola privacy controls.",
    },
    {
      target: '[data-tour="profile-tour-replay"]',
      title: "Replay product tour",
      body: "Restart the full platform guide from Home whenever you want another walkthrough.",
    },
    {
      target: '[data-tour="profile-membership"] a',
      title: "Membership and billing",
      body: "Open your membership page to inspect billing and Nikola credit availability.",
    },
    {
      target: "button",
      label: "Delete account",
      title: "Delete account",
      body: "Open the permanent account-deletion confirmation. It covers your profile, games, analyses, and Nikola history; read it before proceeding.",
    },
  ],
  settings: [
    setting(
      "general",
      "Remember where I left off",
      "Remember page state so returning to a workspace restores your previous position and choices.",
    ),
    setting(
      "general",
      "Animations and smooth scrolling",
      "Choose whether movement and smooth scrolling are enabled across the workspace.",
    ),
    setting(
      "general",
      "Hide selection steps",
      "Collapse completed format and clock steps when results are shown. Change filters lets you reopen them.",
    ),
    setting("general", "Replay full platform tour", "Restart the guided walkthrough from Home.", "button"),
    setting(
      "general",
      "Clear remembered page state",
      "Clear remembered workspace positions and filters so pages can use their defaults again.",
      "button",
    ),
    setting("general", "Reset this section", "Restore the default General preferences for this section.", "button"),
    setting(
      "boards",
      "Shared defaults",
      "Configure the board preferences used by contexts that have no custom overrides.",
      '[role="tab"]',
    ),
    setting(
      "boards",
      "Game Review",
      "Choose the Game Review board context before adjusting its board preferences.",
      '[role="tab"]',
    ),
    setting(
      "boards",
      "Free Board",
      "Choose the Free Board context to customize its board preferences independently.",
      '[role="tab"]',
    ),
    setting(
      "boards",
      "Patterns",
      "Choose the Patterns board context to customize how evidence boards appear.",
      '[aria-label="Board settings context"] [role="tab"]',
    ),
    setting(
      "boards",
      "Nikola",
      "Choose the Nikola board context to customize boards inside coaching conversations.",
      '[role="tab"]',
    ),
    setting(
      "boards",
      "Reset shared board defaults",
      "Restore the shared board preferences to their defaults.",
      "button",
    ),
    {
      target: '[aria-label="Evaluation depth"] button',
      title: "Evaluation depth",
      body: "Choose how deeply Nikola evaluates positions on this board. Greater depth takes longer.",
      reveal: "#settings-tab-boards",
    },
    ...(
      [
        ["Move sounds", "Play a sound when navigating or making a move."],
        ["Evaluation bar", "Show the position evaluation alongside the board."],
        [
          "Tactical motifs & missed opportunities",
          "Show verified tactical motifs and missed opportunities in board evidence.",
        ],
        ["Legal destinations", "Show the legal squares available when selecting a piece."],
        ["Move motifs", "Show the verified motifs attached to the current move."],
        ["Principle signals", "Show detected chess-principle signals alongside the board evidence."],
        ["Opponent coaching", "Include coaching evidence for the opponent's moves."],
        ["Verified arrows", "Show arrows for verified move evidence on the board."],
        ["FEN relationships", "Show relationships derived from the exact board position."],
        [
          "All verified relationships",
          "Display all available verified relationships rather than only the focused ones.",
        ],
        ["Board relationship colors", "Tap a relationship to show or hide it in board arrows and badges."],
      ] as const
    ).map(([label, body]): ControlTourStep => ({
      target: `[data-tour="${label}"]`,
      title: label,
      body,
      reveal: "#settings-tab-boards",
    })),
    setting(
      "games",
      "Games to show",
      "Choose whether Games initially shows all imported games or only analyzed games.",
    ),
    setting("games", "Review workflow", "Choose the default workflow used when opening a game review."),
    setting(
      "games",
      "Default game format",
      "Choose the format used when no remembered selection or explicit link overrides it.",
    ),
    setting("games", "Default game clock", "Choose the time limit used by the default Games scope."),
    setting(
      "games",
      "Default game sorting",
      "Choose the initial game-list order when there is no remembered sorting choice.",
    ),
    setting(
      "patterns",
      "Default patterns format",
      "Choose the format used by Patterns when no remembered filter or explicit link supplies one.",
    ),
    setting("patterns", "Default patterns clock", "Choose the clock used by the default Patterns scope."),
    setting(
      "patterns",
      "Default patterns period",
      "Choose the default time period for Patterns where the workspace supports a period filter.",
    ),
    setting(
      "trends",
      "Default trends format",
      "Choose the format used by Trends when no remembered filter or explicit link supplies one.",
    ),
    setting("trends", "Default trends clock", "Choose the clock used by the default Trends scope."),
    setting(
      "trends",
      "Default trends period",
      "Choose the default time period for Trends where the workspace supports a period filter.",
    ),
    setting(
      "analysis",
      "Default analysis depth",
      "Choose the analysis preset initially offered for new analysis work.",
    ),
    setting("analysis", "Default analysis format", "Choose the initial format targeted by the Analyze page."),
    setting("analysis", "Default analysis clock", "Choose the initial clock targeted by the Analyze page."),
    setting(
      "nikola",
      "Personality & instructions",
      "Write preferences for how Nikola explains and coaches. Save your draft to apply it to future requests.",
    ),
    setting("nikola", "Save", "Save your edited Nikola personality and instructions.", "button"),
    setting("nikola", "Context format", "Choose which game format supplies the coaching context shown here."),
    {
      target: 'input[placeholder="e.g. Play calmer rapid games"]',
      title: "Coaching goals",
      body: "Add a personal goal to help Nikola tailor its coaching. These notes are self-reported context rather than verified chess facts.",
      reveal: "#settings-tab-nikola",
    },
    {
      target: 'input[placeholder="e.g. I stay calm in endgames"]',
      title: "Self-reported strengths",
      body: "Record strengths you believe you have so Nikola can consider your perspective alongside game evidence.",
      reveal: "#settings-tab-nikola",
    },
    {
      target: 'input[placeholder="e.g. I rush below one minute"]',
      title: "Self-reported weaknesses",
      body: "Record habits you want to improve. Nikola treats these notes as your own assessment.",
      reveal: "#settings-tab-nikola",
    },
    {
      target: '[data-tour="COACHING MEMORY"]',
      title: "Coaching memory",
      body: "Allow Nikola to recall useful context across conversations. Turning this off deletes saved coaching memory.",
      reveal: "#settings-tab-nikola",
    },
    {
      target: '[data-tour="WEB RESEARCH"]',
      title: "Web research",
      body: "Let Nikola ask before consulting current public chess sources when your own games cannot answer the question.",
      reveal: "#settings-tab-nikola",
    },
    setting(
      "nikola",
      "Memory retention",
      "Choose how long cross-session coaching memory remains available. Enable coaching memory to use this control.",
      "select",
    ),
    setting("nikola", "Review memory", "Inspect the coaching context Nikola has saved across conversations.", "button"),
    setting(
      "nikola",
      "Delete saved memory",
      "Delete saved coaching memory when you want Nikola to stop recalling that context.",
      "button",
    ),
    setting(
      "nikola",
      "Chat history retention",
      "Choose automatic deletion after a period or keep conversations until you delete them.",
      "select",
    ),
    setting("nikola", "Download chats", "Download your saved conversations for your own records.", "button"),
    setting(
      "nikola",
      "Delete chat history",
      "Request removal of saved chat history. Read any confirmation before proceeding.",
      "button",
    ),
    setting(
      "notifications",
      "Optional Chessla newsletter",
      "Choose whether to receive the optional Chessla newsletter.",
    ),
  ],
  help: [
    {
      target: 'a[href^="mailto:support@chessla.net"]',
      title: "Report a bug",
      body: "Open an email draft to Chessla support. Describe the problem and the steps needed to reproduce it.",
    },
    {
      target: '[aria-label="Help sections"] a[href="#glossary"]',
      title: "Chessla glossary",
      body: "Jump to explanations of the terms used across reviews and coaching.",
    },
    {
      target: '[aria-label="Help sections"] a[href="#pattern-icons"]',
      title: "Pattern and tactical icons",
      body: "Jump to the guide for the pattern symbols shown on evidence boards.",
    },
    {
      target: '[aria-label="Help sections"] a[href="#moves"]',
      title: "Move evidence",
      body: "Read how move labels and evidence indicators should be interpreted.",
    },
    {
      target: '[aria-label="Help sections"] a[href="#review"]',
      title: "Review and coaching",
      body: "Read the meanings of critical moments, patterns, focuses, and verified lines.",
    },
    {
      target: 'a[aria-label^="Learn about "]',
      title: "Read a chess reference",
      body: "Open the linked Chess.com explanation for the displayed chess concept.",
    },
    {
      target: 'a[href$="/privacy"]',
      title: "Privacy policy",
      body: "Read the policy explaining how Chessla handles your data.",
    },
    { target: 'a[href$="/terms"]', title: "Terms of service", body: "Read the terms that apply to using Chessla." },
  ],
  about: [
    {
      target: "#about-title",
      title: "Learn from your own games",
      body: "Import your games, review verified moments, then use Patterns to find recurring decisions worth practicing. Game Review and Patterns can each be used independently.",
    },
    {
      target: 'a[href="https://shotdata.io"]',
      title: "Shotdata",
      body: "Open the linked Shotdata site referenced in the founder's background.",
    },
    {
      target: 'a[href="https://br.linkedin.com/in/danton-mariano"]',
      title: "Founder profile",
      body: "Open Danton Mariano's LinkedIn profile to read more about the person behind Chessla.",
    },
  ],
};
